<template>
    <div class="form-group">
        <label class="form-label section-title">{{ $t('messages.audience_segments') }}</label>
        <div class="bordered-container">
            <!-- Переключатель категорий сегментов -->
            <div class="segments-tabs">
                <button v-for="(category, index) in translatedSegmentCategories" :key="index"
                    :class="['tab-button', { active: activeSegmentCategory === index }]"
                    @click="switchSegmentCategory(index)">
                    {{ category.name }}
                    <span class="filter-count">{{ getSelectedCount(category.type) }}</span>
                </button>
            </div>

            <!-- Панель управления -->
            <div class="controls-panel">
                <div class="search-input">
                    <img src="/images/search.svg" alt="search" class="search-icon" />
                    <input type="text" class="form-input" v-model="searchQuery"
                        :placeholder="$t('messages.search_segments_placeholder')" />
                    <button v-if="searchQuery" class="clear-search" @click="clearSearch">
                        <img src="/images/close.svg" alt="clear" />
                    </button>
                </div>
            </div>

            <!-- Основной контент в две колонки -->
            <div class="segments-content">
                <!-- Левая колонка - список сегментов (70%) -->
                <div class="segments-column">
                    <!-- Список доступных сегментов -->
                    <div class="segments-list">
                        <SegmentItem v-for="segment in filteredSegments" :key="segment.id" :segment="segment" :level="0"
                            :expanded-items="expandedItems" :selected="selected"
                            :should-show-description="shouldShowDescription" @add-segment="addSegment"
                            @remove-segment="removeSegment" @toggle-expand="toggleExpand" />
                        <div v-if="filteredSegments.length === 0" class="empty-state">
                            {{ $t('messages.no_available_segments') }}
                        </div>
                    </div>
                </div>

                <!-- Правая колонка - выбранные сегменты (30%) -->
                <div class="selected-column">
                    <div class="selected-header">
                        <div class="selected-label">{{ $t('messages.selected') }}</div>
                        <button class="btn-delete-all" @click="deleteAllSegments">
                            {{ $t('messages.delete_all') }}
                        </button>
                    </div>
                    <div class="selected-content">
                        <div v-for="segment in currentSelectedSegments" :key="segment.id" class="selected-segment">
                            <span class="segment-name">{{ segment.title }}</span>
                            <button class="remove-btn" @click="removeSegment(segment)">
                                <img src="/images/circle-minus.svg" alt="remove" />
                            </button>
                        </div>
                        <div v-if="currentSelectedSegments.length === 0" class="empty-state">
                            {{ $t('messages.no_selected_segments') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SegmentItem from './SegmentItem.vue';

export default {
    name: "AudienceSegments",
    components: {
        SegmentItem
    },
    props: {
        audiences: {
            type: Array,
            default: () => []
        },
        // flat array of selected ids
        selected: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            searchQuery: '',
            expandedItems: new Set(), // Для отслеживания развернутых элементов
            activeSegmentCategory: 0,
            segmentCategories: [
                { name: 'Пол', type: 'gender' },
                { name: 'Возраст', type: 'age' },
                { name: 'Родитель', type: 'parent' },
                { name: 'Поведение', type: 'behavior' },
                { name: 'Интересы', type: 'interest' },
                { name: 'Намерение', type: 'intention' },
                { name: 'Тематика', type: 'subject' }
            ]
        };
    },
    computed: {
        translatedSegmentCategories() {
            return [
                { name: this.$t('messages.gender'), type: 'gender' },
                { name: this.$t('messages.age'), type: 'age' },
                { name: this.$t('messages.parent'), type: 'parent' },
                { name: this.$t('messages.behavior'), type: 'behavior' },
                { name: this.$t('messages.interest'), type: 'interest' },
                { name: this.$t('messages.intention'), type: 'intention' },
                { name: this.$t('messages.subject'), type: 'subject' }
            ];
        },

        // Получаем переведенные сегменты в зависимости от текущего языка.
        // Раньше использовались жёсткие id-диапазоны, теперь определяем принадлежность
        // сегмента к категории по цепочке parent_id (ancestor contains root).
        translatedSegments() {
            const currentLocale = this.$i18n.locale || 'ru';
            const categoryRootIds = {
                gender: 10000,
                age: 20000,
                parent: 30000,
                behavior: 40000,
                interest: 50000,
                intention: 60000,
                subject: 70000
            };
            const result = {};
            Object.keys(categoryRootIds).forEach(category => {
                const rootId = categoryRootIds[category];
                // include audiences that are descendants of the category root
                const categoryAudiences = this.audiences.filter(audience => {
                    // skip the root node itself (we want its children/descendants)
                    const idNum = Number(audience.id);
                    if (idNum === rootId) return false;
                    return this.isDescendantOfRoot(audience, rootId);
                });
                result[category] = this.formatHierarchicalSegments(categoryAudiences, category, currentLocale);
            });
            return result;
        },

        currentSegments() {
            const categoryType = this.segmentCategories[this.activeSegmentCategory].type;
            return this.translatedSegments[categoryType] || [];
        },

        currentSelectedSegments() {
            const categoryType = this.segmentCategories[this.activeSegmentCategory].type;
            const selectedIds = Array.isArray(this.selected) ? this.selected.map(Number) : [];
            const allSegments = this.translatedSegments[categoryType] || [];
            return selectedIds.map(id => this.findSegmentById(allSegments, id)).filter(s => s !== null);
        },

        currentSelectedSegmentIds() {
            return Array.isArray(this.selected) ? this.selected.map(Number) : [];
        },

        filteredSegments() {
            let filtered = this.currentSegments;
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = this.filterSegmentsRecursively(filtered, query);
            }
            return filtered;
        },

        shouldShowDescription() {
            return true;
        }
    },
    methods: {
        isDescendantOfRoot(audience, rootId) {
            const pid = audience && audience.parent_id ? Number(audience.parent_id) : null;
            if (pid === rootId) return true;
            if (pid) {
                const parent = this.audiences.find(a => Number(a.id) === pid);
                if (parent) return this.isDescendantOfRoot(parent, rootId);
            }
            return false;
        },

        formatSimpleSegments(audiences, type, locale) {
            return audiences.map(audience => ({
                id: Number(audience.id),
                title: audience[`name_${locale}`] || audience.name_ru || audience.name_en || audience.name_kk,
                description: audience[`description_${locale}`] || audience.description_ru || audience.description_en || audience.description_kk
            }));
        },

        formatHierarchicalSegments(audiences, type, locale) {
            const audienceMap = new Map();
            audiences.forEach(audience => {
                audienceMap.set(Number(audience.id), {
                    id: Number(audience.id),
                    parent_id: audience.parent_id ? Number(audience.parent_id) : null,
                    title: audience[`name_${locale}`] || audience.name_ru || audience.name_en || audience.name_kk,
                    description: audience[`description_${locale}`] || audience.description_ru || audience.description_en || audience.description_kk,
                    children: []
                });
            });
            const rootItems = [];
            const categoryRootIds = {
                gender: 10000,
                age: 20000,
                parent: 30000,
                behavior: 40000,
                interest: 50000,
                intention: 60000,
                subject: 70000
            };
            const categoryRootId = categoryRootIds[type];
            audienceMap.forEach(audience => {
                const pid = audience.parent_id !== null ? Number(audience.parent_id) : null;
                if (pid === categoryRootId) {
                    rootItems.push(audience);
                } else if (pid !== null) {
                    const parent = audienceMap.get(pid);
                    if (parent) parent.children.push(audience);
                }
            });
            return rootItems;
        },

        // Collect ids recursively from a segment node
        _collectIds(node) {
            const ids = [];
            if (!node) return ids;
            if (node.id) ids.push(Number(node.id));
            if (node.children && node.children.length) {
                node.children.forEach(c => ids.push(...this._collectIds(c)));
            }
            return ids;
        },

        addSegment(segment) {
            if (!this.isSegmentSelected(segment)) {
                const flat = new Set(Array.isArray(this.selected) ? this.selected.map(Number) : []);
                flat.add(Number(segment.id));
                this.$emit('update:audiences', Array.from(flat));
            }
        },

        removeSegment(segment) {
            const flat = new Set(Array.isArray(this.selected) ? this.selected.map(Number) : []);
            flat.delete(Number(segment.id));
            this.$emit('update:audiences', Array.from(flat));
        },

        isSegmentSelected(segment) {
            const segmentId = typeof segment.id === 'number' ? segment.id : parseInt(segment.id);
            return Array.isArray(this.selected) && this.selected.map(Number).includes(Number(segmentId));
        },

        deleteAllSegments() {
            const categoryType = this.segmentCategories[this.activeSegmentCategory].type;
            const categoryIds = this.translatedSegments[categoryType].flatMap(root => this._collectIds(root));
            const newSelected = (Array.isArray(this.selected) ? this.selected.map(Number) : []).filter(id => !categoryIds.includes(id));
            this.$emit('update:audiences', Array.from(new Set(newSelected)));
        },

        switchSegmentCategory(index) {
            this.activeSegmentCategory = index;
            this.searchQuery = ''; // Очищаем поиск при переключении
            this.expandedItems.clear(); // Очищаем развернутые элементы
        },

        clearSearch() {
            this.searchQuery = '';
            this.expandedItems.clear();
        },

        toggleExpand(item) {
            if (this.expandedItems.has(item.id)) {
                this.expandedItems.delete(item.id);
            } else {
                this.expandedItems.add(item.id);
            }
        },

        findSegmentById(segments, id) {
            const numericId = parseInt(id);
            for (const segment of segments) {
                if (segment.id === numericId || segment.id === id) {
                    return segment;
                }
                if (segment.children && segment.children.length > 0) {
                    const found = this.findSegmentById(segment.children, id);
                    if (found) {
                        return found;
                    }
                }
            }
            return null;
        },

        getSelectedCount(categoryType) {
            const ids = this.translatedSegments[categoryType] ? this.translatedSegments[categoryType].flatMap(root => this._collectIds(root)) : [];
            const sel = Array.isArray(this.selected) ? this.selected.map(Number) : [];
            return sel.filter(id => ids.includes(id)).length;
        },

        filterSegmentsRecursively(segments, query) {
            const result = [];
            for (const segment of segments) {
                const matchesTitle = segment.title.toLowerCase().includes(query);
                const matchesDescription = segment.description && segment.description.toLowerCase().includes(query);
                let matchingChildren = [];
                if (segment.children && segment.children.length > 0) {
                    matchingChildren = this.filterSegmentsRecursively(segment.children, query);
                }
                if (matchesTitle || matchesDescription || matchingChildren.length > 0) {
                    const filteredSegment = { ...segment };
                    if (matchingChildren.length > 0) {
                        filteredSegment.children = matchingChildren;
                        this.expandedItems.add(segment.id);
                    }
                    result.push(filteredSegment);
                }
            }
            return result;
        }
    }
};
</script>

<style lang="scss" scoped>
.bordered-container {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    background: white;
    overflow: hidden;
}

.segments-tabs {
    display: flex;
    border-bottom: 1px solid #e9ecef;
    background: #f8f9fa;

    .tab-button {
        flex: 1;
        padding: 12px 16px;
        background: none;
        border: none;
        border-bottom: 3px solid transparent;
        font-size: 14px;
        font-weight: 500;
        color: #6c757d;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: space-between;

        &:hover {
            color: #495057;
            background: #e9ecef;
        }

        &.active {
            color: #ec4899;
            border-bottom-color: #ec4899;
            background: white;
        }

        .filter-count {
            background: #6c757d;
            color: white;
            border-radius: 12px;
            padding: 2px 8px;
            font-size: 12px;
            font-weight: 600;
            min-width: 20px;
            text-align: center;
        }

        &.active .filter-count {
            background: #ec4899;
            color: white;
        }
    }
}

.controls-panel {
    padding: 16px;
    border-bottom: 1px solid #e9ecef;
    background: white;

    .search-input {
        position: relative;
        width: 100%;

        .search-icon {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            width: 16px;
            height: 16px;
            opacity: 0.6;
        }

        .clear-search {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            border-radius: 50%;
            transition: background-color 0.2s ease;

            &:hover {
                background: #f8f9fa;
            }

            img {
                width: 12px;
                height: 12px;
                opacity: 0.6;
            }
        }

        .form-input {
            width: 100%;
            height: 40px;
            padding: 8px 40px 8px 40px;
            border: 1px solid #e9ecef;
            border-radius: 4px;
            font-size: 14px;
            background: white;
            transition: border-color 0.2s;

            &:focus {
                outline: none;
                border-color: rgba(236, 72, 153, 0.1);
                box-shadow: 0 0 0 1px rgba(236, 72, 153, 0.1);
            }

            &::placeholder {
                color: #6c757d;
            }
        }
    }
}

.segments-content {
    display: flex;
    gap: 0;
    min-height: 400px;
}

.segments-column {
    flex: 0 0 70%;
    border-right: 1px solid #e9ecef;
}

.selected-column {
    flex: 0 0 30%;
    background: #f8f9fa;
    display: flex;
    flex-direction: column;
}

.selected-header {
    padding: 12px;
    border-bottom: 1px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;

    .selected-label {
        font-size: 14px;
        font-weight: 500;
        color: #495057;
    }
}

.selected-content {
    flex: 1;
    padding: 16px;
    overflow-y: auto;

    .selected-segment {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        background: white;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        padding: 8px 12px;
        margin-bottom: 8px;
        font-size: 14px;

        .segment-name {
            color: #495057;
            flex: 1;
            word-break: break-word;
        }

        .remove-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 2px;
            transition: transform 0.2s ease;

            img {
                width: 16px;
                height: 16px;
                opacity: 0.6;
            }

            &:hover {
                transform: scale(1.1);

                img {
                    opacity: 1;
                }
            }
        }
    }

    .empty-state {
        color: #6c757d;
        font-style: italic;
        font-size: 14px;
        text-align: center;
        padding: 20px;
    }
}

.segments-list {
    height: 100%;
    overflow-y: auto;
}

.empty-state {
    padding: 40px 16px;
    text-align: center;
    color: #6c757d;
    font-style: italic;
}
</style>
